import * as Collapsible from '@radix-ui/react-collapsible'
import classNames from 'classnames'
import { Tools } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import type { AgentToolCall } from 'app/components/ai-agent-voice-ops/callDataDebug'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { formatSeconds } from 'app/utils/transcript'

/** A labelled JSON block, styled like the card's ElevenLabs Debug Data panel. */
function JsonSection({ label, value }: { label: string; value: unknown }): ReactElement {
  const text = typeof value === `string` ? value : JSON.stringify(value, null, 2)
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-caption-md text-subtle">{label}</span>
        <div className="h-3.5 text-subtle">
          <CopyToClipboard text={text} />
        </div>
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-default bg-surface-subtle p-3 text-label-xs text-strong">
        {text}
      </pre>
    </div>
  )
}

/**
 * One agent tool call in a call transcript, for super admins (TRK-5934). It reads as a
 * transcript line: the tool in the speaker slot, whether it succeeded beneath, and on
 * expand the arguments it got and the result it gave.
 */
export function AgentToolCallRow({ toolCall }: { toolCall: AgentToolCall }): ReactElement {
  const [open, setOpen] = useState(false)
  const isError = toolCall.result?.isError === true
  const status = !toolCall.result ? `returned no result` : isError ? `failed` : `succeeded`
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="flex px-4 py-2">
      <Tools className="h-6 w-6 shrink-0 text-subtle" strokeWidth={1.5} />
      <div className="ml-3 min-w-0 flex-1">
        <div className="flex items-center">
          <p className="text-heading-xs leading-5 text-strong">{toolCall.toolName}</p>
          {toolCall.timeInCallSecs !== undefined && (
            <p className="ml-2 text-caption-md text-subtle">{formatSeconds(toolCall.timeInCallSecs)}</p>
          )}
        </div>
        <Collapsible.Trigger className="mt-2 flex items-center text-body-sm text-subtle hover:text-strong">
          <span className={classNames(isError && `text-danger-strong`)}>Tool call {status}</span>
          <span className="ml-1">· {open ? `Hide` : `Show`} details</span>
          <ChevronToggle open={open} className="pr-0" chevronClassName="h-4 w-4" />
        </Collapsible.Trigger>
        <Collapsible.Content className="mt-2 flex flex-col gap-3">
          <JsonSection label="Arguments" value={toolCall.params ?? {}} />
          {toolCall.result ? (
            <JsonSection label="Result" value={toolCall.result.value ?? null} />
          ) : (
            <p className="text-body-sm text-subtle">ElevenLabs recorded no result for this call.</p>
          )}
        </Collapsible.Content>
      </div>
    </Collapsible.Root>
  )
}
